import axios from 'axios';
import { keycloak, saveTokens, clearTokens } from '../auth';

export const LEGACY_API_URL =
  import.meta.env.VITE_LEGACY_API_URL || 'https://s.ez.by';

export const legacyApi = axios.create({
  baseURL: LEGACY_API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000,
});

// Reuse Keycloak token (same as main api client)
legacyApi.interceptors.request.use(
  async (config) => {
    if (keycloak.token) {
      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) saveTokens();
      } catch {
        try {
          await new Promise((r) => setTimeout(r, 1000));
          const refreshed = await keycloak.updateToken(5);
          if (refreshed) saveTokens();
        } catch {
          clearTokens();
          keycloak.login();
          return Promise.reject(new Error('Token refresh failed'));
        }
      }
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor for error handling
legacyApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      try {
        await keycloak.updateToken(-1);
        saveTokens();
        error.config.headers.Authorization = `Bearer ${keycloak.token}`;
        return legacyApi(error.config);
      } catch {
        clearTokens();
        keycloak.login();
      }
    }
    if (import.meta.env.DEV) {
      console.error('Legacy API Error:', error.response?.data || error.message);
    }
    return Promise.reject(error);
  },
);

/**
 * Make a GET request to the legacy API
 */
export async function legacyGet<T>(
  cmd: string,
  params?: Record<string, string | number | undefined>,
): Promise<T> {
  const searchParams = new URLSearchParams({ cmd });
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined) {
        searchParams.set(k, String(v));
      }
    });
  }
  const { data } = await legacyApi.get<T>(`/api_bridge.php?${searchParams}`);
  return data;
}

/**
 * Make a POST request to the legacy API
 */
export async function legacyPost<T, B = Record<string, unknown>>(
  cmd: string,
  body: B,
): Promise<T> {
  const { data } = await legacyApi.post<T>(`/api_bridge.php?cmd=${cmd}`, body);
  return data;
}
